/* ==========================================================================
   Points Mall — page-points-mall section (Phase 4 — server-side session)
   --------------------------------------------------------------------------
   Production integration. No mock data, no PinkHouse credentials in the
   browser. The browser never selects an account: identity lives in a
   Laravel session cookie established by /api/auth.

   Data flow (FanHouse app session handoff):
     App opens /pages/points-mall?ph_token=<one-time token, 120s TTL>
       -> this script strips ph_token from the URL immediately
       -> POST { ph_token } to <auth_endpoint> (Laravel /api/auth)
          with credentials:'include'
       -> Laravel calls PinkHouse POST /partner/v1/members/verify-session,
          creates an authenticated Laravel session (SameSite=None cookie)
       -> Laravel responds with the FULL customer/points payload in one
          round trip; this script renders it directly.
       -> subsequent requests send the session cookie (plus the in-memory
          bearer token as a fallback for browsers that block third-party
          cookies). No customer_email/member_id is ever sent.

   Data flow (Shopify-logged-in visitors, no ph_token):
     GET <storefront>/apps/points-mall/auth  (same-origin Shopify App Proxy;
       Shopify appends logged_in_customer_id + HMAC signature)
       -> Laravel verifies the signature and returns a one-time ticket
       -> POST { ticket } to <exchange_endpoint> (Laravel /api/auth/exchange)
          with credentials:'include'
       -> Laravel creates the authenticated session and returns the FULL
          customer/points payload, which this script renders directly.
       -> an attacker can never forge the signed customer identity, so no
          customer_email is ever sent or trusted.

   Redemption flow:
     Shopify's normal cart is read via /cart.js on every cart change.
       -> this script renders a redemption summary (items, total value,
          required/available/remaining points) and enables the CTA.
       -> clicking "Redeem with Points" POSTs only { items } to
          <redeem_endpoint> (Laravel /api/redeem) with credentials.
        -> Laravel resolves the authenticated member server-side,
           revalidates the cart, reserves the points locally and creates a
           fixed-amount Shopify discount code restricted to the Points Mall
           collection and the current customer.
        -> when the session holds a FanHouse checkout_grant (ph_token logins
           from the app), Laravel also exchanges it for a signed-in Shopify
           checkout URL and returns it as checkout.checkout_url.
        -> on success this script opens checkout_url when present (signed-in
           checkout) or redirects to the normal checkout with the discount
           code applied so the customer can check out as before.
   ========================================================================== */
(function () {
  'use strict';

  var DEFAULT_API_ENDPOINT = 'https://points-mall.mrkabir.com/api/points-mall/customer';
  var DEFAULT_AUTH_ENDPOINT = 'https://points-mall.mrkabir.com/api/auth';
  var DEFAULT_REDEEM_ENDPOINT = 'https://points-mall.mrkabir.com/api/redeem';
  var DEFAULT_EXCHANGE_ENDPOINT = 'https://points-mall.mrkabir.com/api/auth/exchange';
  var DEFAULT_PROXY_AUTH_PATH = '/apps/points-mall/auth';

  /* Mirrors the storefront-wide quantity cap enforced by assets/custom.js
     (MAX_QTY = 5 per product). The reward-cart quantity selector never
     offers more than this many units of one product. */
  var PM_MAX_QTY = 5;

  var STRINGS = {
    addToCart: 'Add to cart',
    addToCartError: "We couldn't add this reward to your cart. Please try again.",
    cartUpdateError: "We couldn't update your reward cart. Please try again.",
    qtyLimit: "Sorry, you can't add more than 5 of the same item.",
    notEnoughPoints: 'Not enough points',
    outOfStock: 'Out of stock',
    emptyCart: 'Your reward cart is empty.',
    addRewardToRedeem: 'Add a reward to redeem with points.',
    nonRewardItems: 'Only Points Mall rewards can be redeemed with points.',
    exceedsOrderMax: 'Your reward cart exceeds the maximum points allowed per order.',
    checkingPoints: 'Checking your points...',
    redeemError: "We couldn't prepare your reward checkout right now. Please try again.",
    loginRequired: 'Please log in to view your Points Mall.',
    loginLink: 'Log in',
    notAvailable: 'Points Mall is not available for this customer.',
    loadError: "We couldn't load your points right now. Please try again.",
    memberFallback: 'PinkHouse Member',
    shippingCountryRequired: 'Select a country/region first.',
    shippingCartEmpty: 'Add a reward to your cart first to estimate shipping.',
    shippingCalculating: 'Calculating shipping...',
    shippingNone: 'No shipping rates are available for this destination.',
    shippingAddressInvalid: 'Enter a complete and valid shipping address.',
    shippingError: "We couldn't estimate shipping right now. Please try again.",
    selectShippingPrompt: 'Select a shipping option to continue.',
    sessionExpired: 'Your login link has expired. Please open the mall from the app again.'
  };

  var section = document.querySelector('[data-pm-points-mall]');
  if (!section) return;

  var state = {
    customerEmail: (section.getAttribute('data-pm-customer-email') || '').trim().toLowerCase(),
    apiEndpoint: (section.getAttribute('data-pm-api-endpoint') || '').trim() || DEFAULT_API_ENDPOINT,
    authEndpoint: (section.getAttribute('data-pm-auth-endpoint') || '').trim() || DEFAULT_AUTH_ENDPOINT,
    redeemEndpoint: (section.getAttribute('data-pm-redeem-endpoint') || '').trim() || DEFAULT_REDEEM_ENDPOINT,
    exchangeEndpoint: (section.getAttribute('data-pm-exchange-endpoint') || '').trim() || DEFAULT_EXCHANGE_ENDPOINT,
    proxyAuthPath: (section.getAttribute('data-pm-proxy-auth-path') || '').trim() || DEFAULT_PROXY_AUTH_PATH,
    rewardIds: parseRewardIds(section.getAttribute('data-pm-reward-ids')),
    data: null,
    ready: false,
    sessionToken: '',
    exchanging: false,
    cart: null,
    processing: false,
    shippingEnabled: false,
    shippingCents: 0,
    shippingSelected: false,
    shippingRateId: null,
    shippingRates: []
  };

  function parseRewardIds(raw) {
    var set = new Set();
    String(raw || '')
      .split(',')
      .forEach(function (id) {
        id = parseInt(id, 10);
        if (id > 0) set.add(id);
      });
    return set;
  }

  function isRewardProduct(productId) {
    return state.rewardIds.has(Number(productId));
  }

  var moneyFormat = (window.MinimogSettings && window.MinimogSettings.money_format) || '${{amount}}';

  /* ----------------------------------------------------------------------
     Formatting helpers
     ---------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, format || moneyFormat);
    }
    var value = (Number(cents) / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || moneyFormat).replace(/\{\{\s*amount\s*\}\}/, parts.join('.'));
  }

  function formatPoints(n) {
    return Number(n || 0).toLocaleString('en-US');
  }

  function getPointValueUsd() {
    return (state.data && state.data.points && state.data.points.point_value_usd) || 0;
  }

  /* points = product price / point_value_usd, e.g. $20 -> 100 pts at $0.20 */
  function priceCentsToPoints(cents) {
    var valueUsd = getPointValueUsd();
    if (!valueUsd) return 0;
    var centsPerPoint = valueUsd * 100;
    return Math.round(cents / centsPerPoint);
  }

  function pointsToCents(points) {
    var valueUsd = getPointValueUsd();
    return Math.round(points * valueUsd * 100);
  }

  function setText(selector, value) {
    var el = section.querySelector(selector);
    if (el) el.textContent = value;
  }

  /* ----------------------------------------------------------------------
     Status banner
     ---------------------------------------------------------------------- */
  var statusEl = section.querySelector('[data-pm-error]');

  function showStatus(message) {
    if (!statusEl) return;
    statusEl.textContent = message;
    statusEl.hidden = false;
  }

  /* Login-required state renders as a link to Shopify's customer login.
     Uses the /customer_authentication/login endpoint (Shopify's New
     Customer Accounts): after sign-in Shopify redirects the shopper back
     to the relative path in return_to — i.e. this page. The theme-level
     login template is bypassed entirely by the hosted login flow. */
  function buildLoginUrl() {
    var params = new URLSearchParams(window.location.search);
    params.delete('ph_token');
    var qs = params.toString();
    var returnUrl = window.location.pathname + (qs ? '?' + qs : '');
    return (
      shopifyRoot() +
      'customer_authentication/login?return_to=' +
      encodeURIComponent(returnUrl)
    );
  }

  function showLoginStatus(message) {
    if (!statusEl) return;
    statusEl.textContent = '';
    statusEl.appendChild(document.createTextNode(message + ' '));

    var link = document.createElement('a');
    link.className = 'pm-points-mall__login-link';
    link.href = buildLoginUrl();
    link.textContent = STRINGS.loginLink;
    statusEl.appendChild(link);

    statusEl.hidden = false;
  }

  function clearStatus() {
    if (!statusEl) return;
    statusEl.textContent = '';
    statusEl.hidden = true;
  }

  /* ----------------------------------------------------------------------
     Section 2 + 3 — customer balance card and points summary
     ---------------------------------------------------------------------- */
  function renderMemberAndRules() {
    var data = state.data;
    if (!data) return;

    var name = (data.member && data.member.name) || STRINGS.memberFallback;
    var available = data.points.available || 0;
    var valueUsd = data.points.point_value_usd || 0;

    setText('[data-pm-name]', name);
    setText('[data-pm-available-points]', formatPoints(available) + ' pts');
    setText('[data-pm-available-points-large]', formatPoints(available) + ' pts');
    setText('[data-pm-available-value]', formatMoney(pointsToCents(available)));
    setText('[data-pm-point-rate]', '1 point = ' + formatMoney(Math.round(valueUsd * 100)));

    setText('[data-pm-summary-available-points]', formatPoints(available) + ' pts');
    setText('[data-pm-summary-available-value]', formatMoney(pointsToCents(available)));
    setText('[data-pm-summary-min]', formatPoints(data.rules.min_redeem_points) + ' pts');
    setText('[data-pm-summary-max-order]', formatPoints(data.rules.max_points_per_order) + ' pts');
    setText('[data-pm-summary-max-day]', formatPoints(data.rules.max_points_per_day) + ' pts');

    renderEarning(data);
  }

  /* ----------------------------------------------------------------------
     "How you earn points" — display-only.
     Purchase earn and review reward are calculated by Pink House
     (server-side). We only surface the API-provided values; when the API
     does not provide them we say so instead of inventing a number.
     ---------------------------------------------------------------------- */
  function renderEarning(data) {
    var purchaseEarn = data.earning && data.earning.purchase_earn_rate;
    var reviewReward = data.earning && data.earning.review_reward_points;

    if (purchaseEarn === null || purchaseEarn === undefined) {
      setText('[data-pm-earn-purchase]', 'Managed by Pink House (server-side)');
    } else {
      setText('[data-pm-earn-purchase]', formatPoints(purchaseEarn) + ' points / $1');
    }

    if (reviewReward === null || reviewReward === undefined) {
      setText('[data-pm-earn-review]', 'Managed by Pink House (server-side)');
    } else {
      setText('[data-pm-earn-review]', formatPoints(reviewReward) + ' points');
    }
  }

  /* ----------------------------------------------------------------------
     Section 4 + 5 — product cards and their states
     ---------------------------------------------------------------------- */
  function getCards() {
    return Array.prototype.slice.call(section.querySelectorAll('[data-pm-card]'));
  }

  function buildProductMap() {
    var map = {};
    getCards().forEach(function (card) {
      var titleEl = card.querySelector('[data-pm-card-title]');
      map[card.getAttribute('data-product-id')] = {
        priceCents: parseInt(card.getAttribute('data-price-cents'), 10) || 0,
        available: card.getAttribute('data-available') === 'true',
        title: titleEl ? titleEl.textContent.trim() : ''
      };
    });
    return map;
  }

  function updateCardStates() {
    var ready = state.ready && state.data;

    getCards().forEach(function (card) {
      var priceCents = parseInt(card.getAttribute('data-price-cents'), 10) || 0;
      var available = card.getAttribute('data-available') === 'true';

      var pointsEl = card.querySelector('[data-pm-card-points]');
      var statusEl = card.querySelector('[data-pm-card-status]');
      var btn = card.querySelector('[data-pm-card-atc]');
      var btnText = card.querySelector('[data-pm-card-atc-text]');
      var addToCartLabel =
        (window.MinimogStrings && window.MinimogStrings.addToCart) || STRINGS.addToCart;

      card.classList.remove('pm-product-card--available');
      card.classList.remove('pm-product-card--insufficient');
      card.classList.remove('pm-product-card--sold-out');

      if (!available) {
        card.classList.add('pm-product-card--sold-out');
        if (statusEl) statusEl.textContent = STRINGS.outOfStock;
        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = STRINGS.outOfStock;
        return;
      }

      if (!ready) {
        if (pointsEl) pointsEl.textContent = '\u2013';
        if (statusEl) statusEl.textContent = '';
        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = addToCartLabel;
        return;
      }

      var points = priceCentsToPoints(priceCents);
      if (pointsEl) pointsEl.textContent = formatPoints(points);

      if (state.data.points.available < points) {
        card.classList.add('pm-product-card--insufficient');
        if (statusEl) statusEl.textContent = STRINGS.notEnoughPoints;
        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = STRINGS.notEnoughPoints;
      } else {
        card.classList.add('pm-product-card--available');
        if (statusEl) statusEl.textContent = '';
        if (btn) btn.disabled = false;
        if (btnText) btnText.textContent = addToCartLabel;
      }
    });
  }

  /* ----------------------------------------------------------------------
     Section 7 — redemption preview (uses Shopify's normal cart)
     ---------------------------------------------------------------------- */
  var redeemItemsEl = section.querySelector('[data-pm-redeem-items]');
  var redeemEmptyEl = section.querySelector('[data-pm-redeem-empty]');
  var redeemValueEl = section.querySelector('[data-pm-redeem-value]');
  var redeemTotalEl = section.querySelector('[data-pm-redeem-total]');
  var redeemBalanceEl = section.querySelector('[data-pm-redeem-balance]');
  var redeemRemainingEl = section.querySelector('[data-pm-redeem-remaining]');
  var redeemWarningEl = section.querySelector('[data-pm-redeem-warning]');
  var redeemCta = section.querySelector('[data-pm-redeem-cta]');
  var redeemCtas = section.querySelectorAll('[data-pm-redeem-cta]');
  var redeemCtaInfoEl = section.querySelector('[data-pm-cta-info]');
  var redeemNoticeEl = section.querySelector('[data-pm-redeem-notice]');

  var shippingEl = section.querySelector('[data-pm-shipping]');
  var shippingCountryEl = section.querySelector('[data-pm-shipping-country]');
  var shippingProvinceEl = section.querySelector('[data-pm-shipping-province]');
  var shippingZipEl = section.querySelector('[data-pm-shipping-zip]');
  var shippingCalcEl = section.querySelector('[data-pm-shipping-calc]');
  var shippingStatusEl = section.querySelector('[data-pm-shipping-status]');
  var shippingOptionsEl = section.querySelector('[data-pm-shipping-options]');
  var shippingRowEl = section.querySelector('[data-pm-redeem-shipping-row]');
  var shippingValueEl = section.querySelector('[data-pm-redeem-shipping]');
  var shippingPointsRowEl = section.querySelector('[data-pm-redeem-shipping-points-row]');
  var shippingPointsEl = section.querySelector('[data-pm-redeem-shipping-points]');

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getRewardItems(cart) {
    var items = [];
    var raw = (cart && cart.items) || [];

    raw.forEach(function (item) {
      if (!isRewardProduct(item.product_id)) return;

      var perUnit = priceCentsToPoints(item.price);
      if (perUnit <= 0) return;

      items.push({
        key: item.key,
        variantId: item.id,
        title: item.title,
        quantity: item.quantity,
        points: perUnit * item.quantity,
        linePrice: item.line_price || 0
      });
    });

    return items;
  }

  function cartHasNonReward(cart) {
    var raw = (cart && cart.items) || [];
    return raw.some(function (item) {
      return !isRewardProduct(item.product_id);
    });
  }

  function renderRedemption(cart) {
    if (!state.ready || !state.data) return;

    state.cart = cart;

    var data = state.data;
    var raw = (cart && cart.items) || [];
    var items = getRewardItems(cart);
    var hasNonReward = cartHasNonReward(cart);
    var productPoints = 0;
    var productValue = 0;
    var shippingPoints = 0;
    var i;

    redeemItemsEl.innerHTML = '';
    redeemWarningEl.textContent = '';
    redeemWarningEl.hidden = true;
    redeemNoticeEl.textContent = '';
    redeemNoticeEl.hidden = true;

    for (i = 0; i < items.length; i += 1) {
      productPoints += items[i].points;
      productValue += items[i].linePrice;
    }

    var shippingIncluded = state.shippingEnabled && state.shippingSelected;
    if (shippingIncluded) {
      shippingPoints = priceCentsToPoints(state.shippingCents);
    }

    var totalPoints = productPoints + shippingPoints;
    var totalValue = productValue + (shippingIncluded ? state.shippingCents : 0);

    redeemEmptyEl.hidden = items.length > 0 || hasNonReward;

    if (redeemCtaInfoEl) {
      if (items.length > 0) {
        var totalQty = items.reduce(function (sum, item) {
          return sum + item.quantity;
        }, 0);
        redeemCtaInfoEl.textContent =
          'You have ' + totalQty + ' item' + (totalQty === 1 ? '' : 's') + ' in cart.';
      } else {
        redeemCtaInfoEl.textContent = STRINGS.emptyCart;
      }
    }

    items.forEach(function (item) {
      var li = document.createElement('li');
      li.className = 'pm-redeem__item';
      li.setAttribute('data-pm-key', item.key);
      li.setAttribute('data-pm-variant', String(item.variantId));
      li.setAttribute('data-pm-qty', String(item.quantity));
      li.setAttribute('data-pm-busy', 'false');

      var main = document.createElement('div');
      main.className = 'pm-redeem__item-main';

      var title = document.createElement('span');
      title.className = 'pm-redeem__item-title';
      title.textContent = item.title;

      /* Quantity stepper — same markup/classes as the theme cart drawer
         (snippets/product-qty-input.liquid) so it looks identical. */
      var controls = document.createElement('span');
      controls.className = 'pm-redeem__item-controls';

      var qty = document.createElement('span');
      qty.className = 'm-quantity pm-qty';
      qty.setAttribute('data-pm-qty-stepper', '');

      var minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.name = 'minus';
      minusBtn.className = 'm-quantity__button pm-qty__btn';
      minusBtn.setAttribute('aria-label', 'Decrease quantity for ' + item.title);
      minusBtn.innerHTML =
        '<svg fill="currentColor" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true" focusable="false">' +
        '<path d="M376 232H8c-4.42 0-8 3.58-8 8v32c0 4.42 3.58 8 8 8h368c4.42 0 8-3.58 8-8v-32c0-4.42-3.58-8-8-8z"/>' +
        '</svg>';

      var input = document.createElement('input');
      input.type = 'number';
      input.className = 'm-quantity__input pm-qty__input';
      input.min = '1';
      input.max = String(Math.max(PM_MAX_QTY, item.quantity));
      input.value = String(item.quantity);
      input.setAttribute('aria-label', 'Quantity for ' + item.title);

      var plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.name = 'plus';
      plusBtn.className = 'm-quantity__button pm-qty__btn';
      plusBtn.setAttribute('aria-label', 'Increase quantity for ' + item.title);
      plusBtn.innerHTML =
        '<svg fill="currentColor" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" aria-hidden="true" focusable="false">' +
        '<path d="M376 232H216V72c0-4.42-3.58-8-8-8h-32c-4.42 0-8 3.58-8 8v160H8c-4.42 0-8 3.58-8 8v32c0 4.42 3.58 8 8 8h160v160c0 4.42 3.58 8 8 8h32c4.42 0 8-3.58 8-8V280h160c4.42 0 8-3.58 8-8v-32c0-4.42-3.58-8-8-8z"/>' +
        '</svg>';

      qty.appendChild(minusBtn);
      qty.appendChild(input);
      qty.appendChild(plusBtn);

      /* Remove — inline SVG cross so the glyph centers perfectly
         regardless of font metrics. */
      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'pm-redeem__item-remove';
      removeBtn.setAttribute('aria-label', 'Remove ' + item.title);
      removeBtn.setAttribute('data-pm-remove', '');
      removeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false">' +
        '<path d="M3 3l10 10M13 3L3 13"/>' +
        '</svg>';

      controls.appendChild(qty);
      controls.appendChild(removeBtn);

      main.appendChild(title);
      main.appendChild(controls);

      var points = document.createElement('span');
      points.className = 'pm-redeem__item-points';
      points.textContent = formatPoints(item.points) + ' pts';

      li.appendChild(main);
      li.appendChild(points);
      redeemItemsEl.appendChild(li);
    });

    var available = data.points.available || 0;
    var remaining = available - totalPoints;

    redeemValueEl.textContent = formatMoney(productValue);
    redeemTotalEl.textContent = formatPoints(totalPoints) + ' pts';
    redeemBalanceEl.textContent = formatPoints(available) + ' pts';
    redeemRemainingEl.textContent = formatPoints(Math.max(remaining, 0)) + ' pts';
    redeemRemainingEl.classList.toggle('pm-redeem__total-value--negative', remaining < 0);

    if (shippingRowEl) {
      shippingRowEl.hidden = !shippingIncluded;
      if (shippingIncluded) {
        shippingValueEl.textContent = formatMoney(state.shippingCents);
      }
    }

    if (shippingPointsRowEl) {
      shippingPointsRowEl.hidden = !(shippingIncluded && shippingPoints > 0);
      if (shippingIncluded && shippingPoints > 0) {
        shippingPointsEl.textContent = formatPoints(shippingPoints) + ' pts';
      }
    }

    var message = '';
    var canRedeem = false;
    if (raw.length === 0) {
      message = STRINGS.addRewardToRedeem;
    } else if (hasNonReward) {
      message = STRINGS.nonRewardItems;
    } else if (state.shippingEnabled && !state.shippingSelected) {
      message = STRINGS.selectShippingPrompt;
    } else if (totalPoints <= 0) {
      message = STRINGS.addRewardToRedeem;
    } else if (totalPoints > available) {
      message = STRINGS.notEnoughPoints;
    } else if (data.rules.max_points_per_order && totalPoints > data.rules.max_points_per_order) {
      message = STRINGS.exceedsOrderMax;
    } else {
      canRedeem = true;
    }

    Array.prototype.forEach.call(redeemCtas, function (btn) {
      btn.disabled = !canRedeem;
    });

    if (message) {
      redeemWarningEl.textContent = message;
      redeemWarningEl.hidden = false;
    }
  }

  function fetchCart() {
    fetch(shopifyRoot() + 'cart.js')
      .then(function (res) {
        return res.json();
      })
      .then(renderRedemption)
      .catch(function () {
        /* Cart unavailable — leave the summary empty. */
      });
  }

  /* ----------------------------------------------------------------------
     Offer card add-to-cart (drawer suppressed).
     Card submits are intercepted at capture phase so the theme's
     <product-form> handler never runs: no cart drawer opens and no
     redirect to the cart page. Items land straight in the Shopify cart,
     which the "Your Reward Cart" sidebar mirrors. The header count
     bubble updates via the emitted "cart-update" (MCartTemplate
     subscription), and the still-hidden drawer markup is re-synced so
     it never shows stale lines if the shopper opens it manually.
     ---------------------------------------------------------------------- */
  function showCartError(message) {
    var notification = window.MinimogTheme && window.MinimogTheme.Notification;
    if (notification && typeof notification.show === 'function') {
      notification.show({
        target: document.body,
        method: 'appendChild',
        type: 'error',
        message: message,
        sticky: true
      });
    } else {
      window.alert(message);
    }
  }

  function syncHiddenCartDrawer() {
    var drawer = document.querySelector('m-cart-drawer');
    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
      drawer.onCartDrawerUpdate();
    }
  }

  /* ----------------------------------------------------------------------
     Reward cart line mutations (quantity / remove).
     Sent as FormData through window.fetch so assets/custom.js's quantity
     guard keeps evaluating every call exactly like any other cart
     request. The guard compares current + requested > MAX_QTY even
     though /cart/change.js quantities are absolute, so switches it would
     reject are split into an empty-then-refill pair whose halves each
     satisfy the same rule (e.g. 5+0 <= 5, then add.js 0+4 <= 5). The
     refill must use /cart/add.js with the VARIANT id: once a line is
     zeroed, Shopify deletes it and its old key becomes invalid.
     ---------------------------------------------------------------------- */
  function qtyLimitMessage() {
    return (window.MinimogSettings && window.MinimogSettings.qty_limit_message) || STRINGS.qtyLimit;
  }

  function qtyLimitError() {
    var err = new Error(qtyLimitMessage());
    err.qtyBlocked = true;
    return err;
  }

  function postCartRequest(endpoint, fields) {
    var body = new FormData();
    Object.keys(fields).forEach(function (name) {
      body.append(name, String(fields[name]));
    });

    return fetch(shopifyRoot() + endpoint, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: body
    }).then(function (res) {
      return res.json().then(function (parsed) {
        return { ok: res.ok, body: parsed };
      });
    });
  }

  function postCartChange(key, quantity) {
    return postCartRequest('cart/change.js', { id: key, quantity: quantity });
  }

  function postCartAdd(variantId, quantity) {
    return postCartRequest('cart/add.js', { id: variantId, quantity: quantity });
  }

  /* Decreases/removals go through /cart/update.js: they can never
     breach a per-product maximum, while the custom.js guard coerces
     every change.js quantity to >= 1 (quantity '0' -> falsy -> 1),
     which would wrongly evaluate a removal as "+1" and block lines
     already at MAX_QTY. Increases/additions stay fully guarded. */
  function postCartUpdates(updates) {
    var body = new FormData();
    Object.keys(updates).forEach(function (key) {
      body.append('updates[' + key + ']', String(updates[key]));
    });

    return fetch(shopifyRoot() + 'cart/update.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: body
    }).then(function (res) {
      return res.json().then(function (parsed) {
        return { ok: res.ok, body: parsed };
      });
    });
  }

  function assertCartResult(result) {
    var body = result.body || {};
    if (body.blocked) throw qtyLimitError();
    if (!result.ok || body.status) {
      throw new Error(body.description || body.message || STRINGS.cartUpdateError);
    }
    return body;
  }

  /* The theme pub/sub does not isolate listeners: one throwing handler
     aborts every later subscriber. Always pass an object payload (the
     drawer listener reads .source) and swallow crashes from foreign
     handlers so our own refresh still runs. */
  function announceCartUpdate(payload) {
    if (!window.MinimogEvents || !window.MinimogEvents.emit) return false;
    try {
      window.MinimogEvents.emit('cart-update', payload || { source: 'pm-points-mall' });
      return true;
    } catch (err) {
      return false;
    }
  }

  function updateRewardLine(key, variantId, newQuantity, currentQuantity) {
    var request;
    if (newQuantity < currentQuantity) {
      /* Decrease or removal — quantity only goes down, so the cap
         cannot be breached. */
      var updates = {};
      updates[key] = newQuantity;
      request = postCartUpdates(updates).then(assertCartResult);
    } else if (currentQuantity + newQuantity > PM_MAX_QTY) {
      request = postCartChange(key, 0)
        .then(assertCartResult)
        .then(function () {
          return postCartAdd(variantId, newQuantity).then(assertCartResult);
        });
    } else {
      request = postCartChange(key, newQuantity).then(assertCartResult);
    }

    /* The live cart may have moved on since render — retry via the
       split path, which the guard approves for any target <= MAX_QTY. */
    return request.catch(function (err) {
      if (!(err && err.qtyBlocked) || newQuantity === 0) throw err;
      return postCartChange(key, 0)
        .then(assertCartResult)
        .then(function () {
          return postCartAdd(variantId, newQuantity).then(assertCartResult);
        });
    }).then(function () {
      var announced = announceCartUpdate();
      syncHiddenCartDrawer();
      /* Refresh even when a foreign cart-update listener crashed and
         swallowed our event. */
      if (!announced) fetchCart();
    });
  }

  function setItemControlsDisabled(container, disabled) {
    Array.prototype.forEach.call(
      container.querySelectorAll('.pm-redeem__item-controls button, .pm-redeem__item-controls input'),
      function (el) { el.disabled = disabled; }
    );
  }

  function clampRewardQuantity(value, maxAllowed) {
    var qty = parseInt(value, 10);
    if (!(qty > 0)) qty = 1;
    return Math.min(Math.max(1, qty), maxAllowed);
  }

  function commitRewardQuantity(li, nextQuantity) {
    if (li.getAttribute('data-pm-busy') === 'true') return;

    var key = li.getAttribute('data-pm-key');
    var variantAttr = li.getAttribute('data-pm-variant');
    var current = parseInt(li.getAttribute('data-pm-qty'), 10) || 1;

    if (!key || !(parseInt(variantAttr, 10) > 0)) {
      showCartError(STRINGS.cartUpdateError);
      return;
    }

    /* 0 is a valid target: it removes the line entirely. */
    var next = nextQuantity === 0
      ? 0
      : clampRewardQuantity(nextQuantity, Math.max(PM_MAX_QTY, current));

    if (next > 0) {
      li.querySelector('.pm-redeem__item-controls .pm-qty__input').value = String(next);
    }

    if (next === current) return;

    li.setAttribute('data-pm-busy', 'true');
    setItemControlsDisabled(li, true);

    updateRewardLine(key, parseInt(variantAttr, 10), next, current).catch(function (err) {
      showCartError((err && err.message) || STRINGS.cartUpdateError);
    }).finally(function () {
      li.setAttribute('data-pm-busy', 'false');
      /* A successful change re-renders this list; a failure must
         restore the controls. */
      setItemControlsDisabled(li, false);
    });
  }

  function handleRemoveClick(li) {
    commitRewardQuantity(li, 0);
  }

  function handleStepClick(li, button) {
    var input = li.querySelector('.pm-redeem__item-controls .pm-qty__input');
    if (!input) return;
    var delta = button.getAttribute('name') === 'plus' ? 1 : -1;
    commitRewardQuantity(li, (parseInt(input.value, 10) || 1) + delta);
  }

  function addToCartFromCard(form, wrapper, button) {
    wrapper.classList.add('m-spinner-loading');
    if (button) button.disabled = true;

    fetch(shopifyRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: new FormData(form)
    })
      .then(function (res) {
        return res.json().then(function (body) {
          /* Shopify errors carry {status, message, description};
             custom.js's quantity guard answers 200 with {blocked:true}. */
          if (body.blocked) throw qtyLimitError();
          if (!res.ok || body.status) {
            throw new Error(body.description || body.message || STRINGS.addToCartError);
          }
          return body;
        });
      })
      .then(function (addedItem) {
        /* Same event the theme emits on ATC: refreshes the reward
           sidebar (subscriber below), the header count bubble and the
           drawer markup — without opening anything. */
        var announced = announceCartUpdate(addedItem);
        syncHiddenCartDrawer();
        if (!announced) fetchCart();
      })
      .catch(function (err) {
        showCartError((err && err.message) || STRINGS.addToCartError);
      })
      .finally(function () {
        wrapper.classList.remove('m-spinner-loading');
        if (button) button.disabled = false;
      });
  }

  function handleOfferFormSubmit(event) {
    var form = event.target;
    if (!form || form.nodeType !== 1) return;

    /* Only offer-card forms inside this section. */
    if (!form.classList.contains('pm-product-form__form')) return;
    if (!section.contains(form)) return;

    event.preventDefault();
    event.stopPropagation();

    addToCartFromCard(
      form,
      form.closest('product-form') || form,
      form.querySelector('[data-pm-card-atc]')
    );
  }

  /* ----------------------------------------------------------------------
     Shipping estimation (optional) — real Shopify rates for the customer's
     destination, only ever fetched when shipping redemption is enabled.
     ---------------------------------------------------------------------- */
  function renderShippingStatus(message) {
    if (!shippingStatusEl) return;
    shippingStatusEl.textContent = message;
    shippingStatusEl.hidden = !message;
  }

  /* Shipping estimation must be sent as query params on the Cart API's
     shipping-rate endpoints (the cart's stored shipping address is only set
     at checkout — POSTing shipping_address to /cart/update.js is ignored).
     Country must be the full country name (e.g. "United States"), not the
     ISO code. */
  function shippingValidationError(body) {
    var messages = [];
    if (body && typeof body === 'object') {
      Object.keys(body).forEach(function (key) {
        var value = body[key];
        if (Array.isArray(value)) {
          value.forEach(function (msg) {
            if (typeof msg === 'string') messages.push(msg);
          });
        } else if (typeof value === 'string') {
          messages.push(value);
        }
      });
    }
    return new Error(messages.length > 0 ? messages.join(' ') : STRINGS.shippingAddressInvalid);
  }

  function buildShippingAddressParams() {
    var params = new URLSearchParams();
    var country = (shippingCountryEl && shippingCountryEl.value || '').trim();
    var zip = (shippingZipEl ? (shippingZipEl.value || '').trim() : '');
    var province = (shippingProvinceEl ? (shippingProvinceEl.value || '').trim() : '');

    if (country) params.set('shipping_address[country]', country);
    if (zip) params.set('shipping_address[zip]', zip);
    if (province) params.set('shipping_address[province]', province);

    return params.toString();
  }

  function prepareShippingRates() {
    var params = buildShippingAddressParams();

    return fetch(shopifyRoot() + 'cart/prepare_shipping_rates.json' + (params ? '?' + params : ''), {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return null; }).then(function (body) {
          throw shippingValidationError(body);
        });
      }
      return res.json().catch(function () { return null; });
    });
  }

  function fetchAsyncShippingRates() {
    var params = buildShippingAddressParams();

    return fetch(shopifyRoot() + 'cart/async_shipping_rates.json' + (params ? '?' + params : ''), {
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) {
        return res.json().catch(function () { return null; }).then(function (body) {
          throw shippingValidationError(body);
        });
      }
      return res.json().catch(function () { return null; });
    });
  }

  function waitForShippingRates(tries, delay) {
    tries = tries || 10;
    delay = delay || 500;

    return new Promise(function (resolve, reject) {
      function poll(remaining) {
        fetchAsyncShippingRates().then(function (body) {
          var rates = (body && body.shipping_rates) || [];

          if (rates.length > 0 || remaining <= 0) {
            resolve(rates);
            return;
          }

          setTimeout(function () {
            poll(remaining - 1);
          }, delay);
        }).catch(reject);
      }

      poll(tries);
    });
  }

  function chooseShippingRate(rate) {
    if (!state.shippingEnabled) return;

    state.shippingRateId = String(rate.id);
    state.shippingCents = Math.max(0, Math.round(parseFloat(rate.price || 0) * 100));
    state.shippingSelected = true;

    if (shippingOptionsEl) {
      Array.prototype.slice.call(shippingOptionsEl.children).forEach(function (option) {
        var radio = option.querySelector('input');
        var active = radio && String(radio.value) === state.shippingRateId;
        option.classList.toggle('pm-shipping__option--active', !!active);
        if (active) radio.checked = true;
      });
    }

    renderShippingStatus('');
    renderRedemption(state.cart);
  }

  function renderShippingOptions(rates) {
    if (!shippingOptionsEl) return;

    shippingOptionsEl.innerHTML = '';
    shippingOptionsEl.hidden = rates.length === 0;

    if (rates.length === 0) return;

    rates.forEach(function (rate, index) {
      var id = String(rate.id || rate.code || rate.name || 'rate-' + index);

      var option = document.createElement('label');
      option.className = 'pm-shipping__option';

      var radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'pm-shipping-rate';
      radio.value = id;
      radio.addEventListener('change', function () {
        chooseShippingRate(rate);
      });

      var title = document.createElement('span');
      title.className = 'pm-shipping__option-label';
      title.textContent = String(rate.title || rate.handle || 'Shipping');

      var price = document.createElement('span');
      price.className = 'pm-shipping__option-price';
      price.textContent = formatMoney(Math.max(0, Math.round(parseFloat(rate.price || 0) * 100)));

      option.appendChild(radio);
      option.appendChild(title);
      option.appendChild(price);

      if (state.shippingSelected && id === state.shippingRateId) {
        radio.checked = true;
        option.classList.add('pm-shipping__option--active');
        state.shippingCents = Math.max(0, Math.round(parseFloat(rate.price || 0) * 100));
      }

      shippingOptionsEl.appendChild(option);
    });
  }

  function refreshShippingRates() {
    if (!state.shippingEnabled) return Promise.resolve([]);

    var country = (shippingCountryEl && shippingCountryEl.value || '').trim();
    if (!country) return Promise.resolve([]);

    return prepareShippingRates()
      .then(waitForShippingRates)
      .then(function (rates) {
        state.shippingRates = rates || [];
        renderShippingOptions(state.shippingRates);
        renderRedemption(state.cart);
        return state.shippingRates;
      });
  }

  function calculateShipping() {
    if (!state.shippingEnabled) return;

    var country = (shippingCountryEl && shippingCountryEl.value || '').trim();
    if (!country) {
      renderShippingStatus(STRINGS.shippingCountryRequired);
      return;
    }

    if (!state.cart || (state.cart.item_count || 0) === 0) {
      renderShippingStatus(STRINGS.shippingCartEmpty);
      return;
    }

    renderShippingStatus(STRINGS.shippingCalculating);
    if (shippingCalcEl) shippingCalcEl.disabled = true;

    refreshShippingRates()
      .then(function (rates) {
        if (shippingCalcEl) shippingCalcEl.disabled = false;
        renderShippingStatus(rates.length === 0 ? STRINGS.shippingNone : '');
      })
      .catch(function (err) {
        if (shippingCalcEl) shippingCalcEl.disabled = false;
        renderShippingStatus((err && err.message) || STRINGS.shippingError);
      });
  }

  /* ----------------------------------------------------------------------
     Session handoff — consume ?ph_token=<one-time token>
     The app opens the mall with a single-use token (120s TTL). It must be
     exchanged immediately: this script POSTs it to Laravel /api/auth and
     Laravel calls PinkHouse verify-session server-side. On success Laravel
     creates an authenticated session cookie and returns the FULL customer
     payload, which is rendered directly (no second round trip).

     The token is stripped from the URL the moment it is read (before the
     network round-trip) so analytics beacons firing while the verify request
     is in flight cannot capture it. It is never persisted anywhere.
     ---------------------------------------------------------------------- */
  function readSessionToken() {
    try {
      return new URLSearchParams(window.location.search).get('ph_token') || '';
    } catch (err) {
      return '';
    }
  }

  function clearSessionToken() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (!params.has('ph_token')) return;
      params.delete('ph_token');
      var qs = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash
      );
    } catch (err) {
      /* History unavailable — leave the URL untouched. */
    }
  }

  function consumeSessionToken(token) {
    return fetch(state.authEndpoint, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ph_token: token })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('verify failed');
        return res.json();
      })
      .then(function (json) {
        /* In-memory bearer token only — used as a fallback for browsers
           that block third-party cookies. Never persisted. */
        state.sessionToken = json.token || '';

        /* The auth response carries the same member/points/rules/earning/
           daily structure as GET /api/points-mall/customer, so render it
           immediately without another API call. */
        applyCustomerData(json);

        return true;
      });
  }

  function applyCustomerData(json) {
    state.data = json;
    state.ready = true;

    state.shippingEnabled = !!(json.rules && json.rules.shipping_redemption_enabled);
    if (!state.shippingEnabled) {
      state.shippingCents = 0;
      state.shippingSelected = false;
      state.shippingRateId = null;
    }
    if (shippingEl) shippingEl.hidden = !state.shippingEnabled;

    clearStatus();
    renderMemberAndRules();
    updateCardStates();
    fetchCart();
  }

  /* ----------------------------------------------------------------------
      Load the current customer's data. Identity is resolved server-side —
      no email/ID is ever sent.

      Fast path (single round trip to first render): the Shopify App Proxy
      handshake GET <storefront>/apps/points-mall/auth is same-origin and
      signed by Shopify with logged_in_customer_id, and returns the FULL
      customer/points payload plus a one-time ticket. The payload renders
      immediately; the ticket is then exchanged in the background (POST
      /api/auth/exchange, skip_payload) to establish the Laravel session
      cookie used by later actions (redeem / refresh).
      ---------------------------------------------------------------------- */
  function authHeaders() {
    var headers = { Accept: 'application/json' };
    if (state.sessionToken) {
      headers.Authorization = 'Bearer ' + state.sessionToken;
    }
    return headers;
  }

  function loadCustomerData() {
    startShopifyExchange(true);
  }

  function startShopifyExchange(renderPayload) {
    if (state.exchanging) return Promise.resolve();
    state.exchanging = true;

    var proxyUrl =
      state.proxyAuthPath.indexOf('http') === 0
        ? state.proxyAuthPath
        : window.location.origin + state.proxyAuthPath;

    return fetch(proxyUrl, {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('proxy unavailable');
        return res.json();
      })
      .then(function (json) {
        if (renderPayload) {
          applyCustomerData(json);
        }
        if (!json.ticket) throw new Error('no ticket');
        return fetch(state.exchangeEndpoint, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ ticket: json.ticket, skip_payload: true })
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error('exchange failed');
        return res.json();
      })
      .then(function (json) {
        state.sessionToken = json.token || '';
      })
      .catch(function () {
        /* Not logged in to Shopify, proxy not configured, or ticket expired. */
        if (renderPayload) {
          showLoginStatus(STRINGS.loginRequired);
          updateCardStates();
        }
      })
      .then(function () {
        state.exchanging = false;
      });
  }

  /* ----------------------------------------------------------------------
     Redeem CTA — Phase 2B: validate, reserve and create the Shopify
     discount code against Laravel (/api/redeem). Sends only the real
     Shopify cart line items, then redirects to checkout with the code
     applied.
     ---------------------------------------------------------------------- */
  var redeemCtaLabel = redeemCta ? redeemCta.textContent : 'Redeem with Points';

  function showNotice(message, isError) {
    redeemNoticeEl.textContent = message;
    redeemNoticeEl.classList.toggle('pm-redeem__notice--error', !!isError);
    redeemNoticeEl.hidden = false;
  }

  function setProcessing(processing) {
    state.processing = processing;
    Array.prototype.forEach.call(redeemCtas, function (btn) {
      btn.disabled = processing;
      btn.textContent = processing ? STRINGS.checkingPoints : redeemCtaLabel;
    });
  }

  function goToCheckoutWithCodes(codes) {
    var clean = (codes || []).filter(function (code) {
      return typeof code === 'string' && code !== '';
    });
    var query = clean.map(function (code) {
      return 'discount=' + encodeURIComponent(code);
    }).join('&');

    window.location.assign(shopifyRoot() + 'checkout' + (query ? '?' + query : ''));
  }

  function postRedeem(body) {
    return fetch(state.redeemEndpoint, {
      method: 'POST',
      credentials: 'include',
      headers: Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        authHeaders()
      ),
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, status: res.status, json: json };
      });
    });
  }

  /* Both the in-section CTA (desktop) and the mobile sticky CTA bar
     trigger redemption. Wire each one independently so the right
     button is shown/hidden via CSS without JS branching. */
  Array.prototype.forEach.call(redeemCtas, function (btn) {
    btn.addEventListener('click', function () {
      if (!state.ready || state.processing || btn.disabled) return;

      var cart = state.cart;
      var raw = (cart && cart.items) || [];
      if (raw.length === 0) return;

      var items = raw.map(function (item) {
        return {
          variant_id: item.variant_id,
          quantity: item.quantity
        };
      });

      /* Identity is resolved server-side from the session — only cart
         contents are sent. */
      var body = {
        items: items
      };

      if (state.shippingEnabled && state.shippingSelected) {
        body.shipping_cents = state.shippingCents;
      }

      setProcessing(true);

      postRedeem(body)
        .then(function (result) {
          /* Session may not be established yet (silent background
             exchange failed or expired) — re-run the handshake and retry
             the redemption exactly once. */
          if (result.status === 401) {
            return startShopifyExchange(false).then(function () {
              return postRedeem(body);
            });
          }
          return result;
        })
        .then(function (result) {
          if (result.ok && result.json && result.json.success) {
            var checkout = result.json.checkout || {};

            /* App/WebView members: Laravel exchanged the checkout_grant for
               a Shopify checkout URL that opens already signed in — prefer
               it over the plain checkout redirect. */
            if (checkout.checkout_url) {
              window.location.assign(checkout.checkout_url);
              return;
            }

            var codes = checkout.discount_codes;
            if (Array.isArray(codes) && codes.length > 0) {
              goToCheckoutWithCodes(codes);
              return;
            }
            if (checkout.discount_code) {
              goToCheckoutWithCodes([checkout.discount_code]);
              return;
            }
            showNotice(STRINGS.redeemError, true);
          } else {
            showNotice((result.json && result.json.message) || STRINGS.redeemError, true);
          }
        })
        .catch(function () {
          showNotice(STRINGS.redeemError, true);
        })
        .then(function () {
          setProcessing(false);
        });
    });
  });

  /* ----------------------------------------------------------------------
     Init
     ---------------------------------------------------------------------- */
  function init() {
    if (section.getAttribute('data-pm-init') === 'true') return;
    section.setAttribute('data-pm-init', 'true');

    updateCardStates();

    if (shippingCalcEl) {
      shippingCalcEl.addEventListener('click', calculateShipping);
    }

    if (shippingCountryEl) {
      /* A new destination invalidates any previously quoted rate. */
      shippingCountryEl.addEventListener('change', function () {
        state.shippingRates = [];
        state.shippingSelected = false;
        state.shippingRateId = null;
        state.shippingCents = 0;
        renderShippingOptions([]);
        renderRedemption(state.cart);
      });
    }

    /* Offer cards: add silently to the cart — no theme drawer, the
       "Your Reward Cart" sidebar is the only visible feedback. Capture
       phase so this runs before <product-form>'s own submit handler. */
    document.addEventListener('submit', handleOfferFormSubmit, true);

    /* Reward sidebar: stepper buttons, typed quantities and remove —
       delegated, because the list is rebuilt on every cart change. */
    redeemItemsEl.addEventListener('click', function (event) {
      var btn = event.target.closest
        ? event.target.closest('[data-pm-remove], .pm-qty__btn')
        : null;
      if (!btn || !redeemItemsEl.contains(btn)) return;

      event.preventDefault();
      var li = btn.closest('.pm-redeem__item');
      if (!li) return;

      if (btn.hasAttribute('data-pm-remove')) {
        handleRemoveClick(li);
      } else {
        handleStepClick(li, btn);
      }
    });

    redeemItemsEl.addEventListener('change', function (event) {
      var input = event.target.closest
        ? event.target.closest('.pm-qty__input')
        : null;
      if (!input || !redeemItemsEl.contains(input)) return;

      var li = input.closest('.pm-redeem__item');
      if (li) commitRewardQuantity(li, parseInt(input.value, 10));
    });

    if (window.MinimogEvents && window.MinimogEvents.subscribe) {
      /* Keep the redemption preview in sync with Shopify's cart
         (add / quantity change / remove all emit "cart-update").
         Always re-fetch so we read the authoritative cart, and reset any
         quoted shipping rate because the cart value changed. */
      window.MinimogEvents.subscribe('cart-update', function () {
        state.shippingRates = [];
        state.shippingSelected = false;
        state.shippingRateId = null;
        state.shippingCents = 0;
        renderShippingOptions([]);
        fetchCart();
      });
    }

    /* Session handoff first: the token is single-use with a 120s TTL, so it
       must be verified before anything else. Strip it from the URL right
       away (it is already captured in sessionToken) so no beacon or history
       entry keeps it, then verify. On success /api/auth has already returned
       and rendered the full customer payload. Falls back to the session-
       cookie flow when there is no token or verification fails
       (expired/burned). */
    var sessionToken = readSessionToken();
    if (sessionToken) {
      clearSessionToken();
      consumeSessionToken(sessionToken).catch(function () {
        showStatus(STRINGS.sessionExpired);
        loadCustomerData();
      });
      return;
    }

    loadCustomerData();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
